import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, FileChips, formatDate, DeadlineWarning, projectInfo, UploadProgress, dropProps, deadlineLabel, ServiceLists, parsePrice } from "@/components/shared";
import { MessageThread } from "@/components/ProjectMessages";
import { ArrowLeft, Paperclip, X, FileText, Send, Pencil, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const LATE_OFFER_EMAILS = ["huseyin@fhsahne.com.tr"];

export default function SupplierRequestDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const fileInput = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => {
    api.get(`/requests/${id}`).then((r) => {
      setReq(r.data);
      if (r.data.currency) setCurrency(r.data.currency);
    }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const list = Array.from(e?.target?.files || e || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const data = await uploadSmart(list, setProgress);
      setFiles((f) => [...f, ...data]);
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const startEdit = () => {
    const o = req.my_offer;
    setPrice(String(o.price));
    setCurrency(o.currency);
    setDescription(o.description || "");
    setFiles(o.files || []);
    setErrors({});
    setEditing(true);
  };

  const submit = async () => {
    const e = {};
    const p = parsePrice(price);
    if (!p || p <= 0) e.price = "Geçerli bir fiyat girin";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      const payload = { price: p, currency, description, file_ids: files.map((f) => f.id) };
      if (editing) {
        await api.patch(`/offers/${req.my_offer.id}`, payload);
        toast.success("Teklifiniz güncellendi");
        setEditing(false);
      } else {
        await api.post(`/requests/${id}/offers`, payload);
        toast.success("Teklifiniz başarıyla iletildi");
      }
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!req) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const expired =
    req.status === "suresi_doldu" ||
    now >= new Date(`${req.deadline}T${req.deadline_time ? req.deadline_time + ":00" : "23:59:59"}+03:00`).getTime();
  const canLate = LATE_OFFER_EMAILS.some((e) => [user?.username, user?.contact_email, user?.email].filter(Boolean).map((x) => x.toLowerCase()).includes(e));
  const blocked = expired && !canLate && !req.late_allowed;
  const canRevise = req.my_offer && req.my_offer.status === "pending" && !blocked;
  const canRebid = req.can_rebid && !blocked;
  const showForm = !blocked && (editing || !req.my_offer);

  return (
    <div className="space-y-8 fade-in max-w-3xl">
      <div>
        <Link to="/tedarikci/talepler" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-supplier-requests">
          <ArrowLeft size={15} /> Taleplere Dön
        </Link>
        <div className="mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter" data-testid="supplier-request-title">{req.title}</h1>
            <StatusBadge status={req.status} />
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-2 text-sm text-slate-500">
            {req.project && <span data-testid="supplier-project-info">{projectInfo(req.project)}</span>}
            <span>Son tarih: {deadlineLabel(req)}</span>
            {expired ? <span className="text-xs text-red-500 font-medium" data-testid="expired-label">İhale Sona Erdi</span> : <DeadlineWarning deadline={req.deadline} time={req.deadline_time} />}
          </div>
        </div>
      </div>

      <div className="border border-slate-200 p-5 space-y-3">
        <div className="text-xs uppercase tracking-wider text-slate-500">Talep Metni</div>
        <p className="text-sm text-slate-600 whitespace-pre-line" data-testid="supplier-request-message">{req.message}</p>
        <ServiceLists included={req.included_services} excluded={req.excluded_services} />
        <div className="text-xs uppercase tracking-wider text-slate-500 pt-2">Ekli Dosyalar</div>
        <FileChips files={req.files} />
      </div>

      {req.my_offer && !editing ? (
        <div className="border border-slate-200 p-5 space-y-4" data-testid="my-offer-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-heading text-xl font-bold tracking-tight">Verdiğiniz Teklif</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={req.my_offer.status} />
              {canRevise && (
                <Button variant="outline" size="sm" onClick={startEdit} data-testid="revise-offer-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 transition-colors duration-200">
                  <Pencil size={13} /> Teklifi Revize Et
                </Button>
              )}
              {canRebid && (
                <Button size="sm" onClick={startEdit} data-testid="rebid-offer-button" className="rounded-none bg-amber-500 hover:bg-amber-400 text-white gap-1.5 font-semibold transition-colors duration-200" title="İhale yeniden teklif almaya açıldı, yeni teklif verebilirsiniz">
                  <Pencil size={13} /> Yeni Teklif Ver
                </Button>
              )}
            </div>
          </div>
          <div className="font-heading font-extrabold text-3xl tracking-tight" data-testid="my-offer-price">
            {req.my_offer.price.toLocaleString("tr-TR")} <span className="text-base font-medium text-slate-500">{req.my_offer.currency}</span>
          </div>
          {req.my_offer.description && <p className="text-sm text-slate-600 whitespace-pre-line">{req.my_offer.description}</p>}
          <FileChips files={req.my_offer.files} />
          <p className="text-xs text-slate-500">
            Teklif tarihi: {formatDate(req.my_offer.created_at)}
            {req.my_offer.updated_at && <> · Son revizyon: {formatDate(req.my_offer.updated_at)}</>}
          </p>
          {(req.my_offer.history || []).length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="offer-history-panel">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
                <History size={13} /> Önceki Fiyatlar
              </div>
              {[...req.my_offer.history].reverse().map((h, i) => (
                <div key={i} className="flex items-center justify-between text-sm text-slate-500" data-testid={`offer-history-item-${i}`}>
                  <span className="line-through">{h.price.toLocaleString("tr-TR")} {h.currency}</span>
                  <span className="text-xs">{formatDate(h.revised_at)} tarihinde revize edildi</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : !showForm ? (
        <div className="border border-red-500/20 bg-red-500/5 p-5" data-testid="expired-panel">
          <p className="text-sm text-red-500">Bu talebin süresi dolmuştur, artık teklif verilemez.</p>
        </div>
      ) : (
        <div className="border border-slate-200 p-5 space-y-5" data-testid="offer-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold tracking-tight">{editing ? "Teklifinizi Revize Edin" : "Teklifinizi Verin"}</h2>
            {editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="cancel-revise-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 transition-colors duration-200">
                <X size={13} /> Vazgeç
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="price">Fiyat</Label>
              <Input id="price" data-testid="offer-price-input" type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="örn. 980.488 veya 980.488,50" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
              {price.trim() && !isNaN(parsePrice(price)) && (
                <p className="text-xs text-emerald-700 font-medium" data-testid="price-preview">
                  = {parsePrice(price).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                </p>
              )}
              {errors.price && <p className="text-red-500 text-sm" data-testid="error-price">{errors.price}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Para Birimi</Label>
              {req.currency ? (
                <div className="h-11 flex items-center px-3 border border-slate-200 bg-[#F5F7FA] text-sm" data-testid="offer-currency-locked">
                  {req.currency} <span className="text-slate-500 ml-2 text-xs">(talep sahibi belirledi)</span>
                </div>
              ) : (
                <Select value={currency} onValueChange={(v) => { if (v) setCurrency(v); }}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" data-testid="offer-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="desc">Açıklama</Label>
            <Textarea id="desc" data-testid="offer-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Teklifinizle ilgili detaylar, teslim süresi, geçerlilik süresi..." className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label>Dosya Ekle (opsiyonel)</Label>
            <input ref={fileInput} type="file" multiple className="hidden" onChange={onFiles} data-testid="offer-file-input" />
            <button type="button" onClick={() => fileInput.current?.click()} {...dropProps(onFiles, setDragOver)} data-testid="offer-file-dropzone" className={`w-full border border-dashed transition-colors duration-200 p-6 flex flex-col items-center gap-2 ${dragOver ? "border-slate-900 bg-slate-100 text-slate-900" : "border-slate-300 bg-[#F5F7FA] hover:border-slate-400 text-slate-500"}`}>
              <Paperclip size={18} />
              <span className="text-sm">{uploading ? "Yükleniyor..." : dragOver ? "Dosyaları buraya bırakın" : "Teklif dosyalarınızı ekleyin veya sürükleyip bırakın"}</span>
            </button>
            {uploading && <UploadProgress progress={progress} />}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 border border-slate-200 bg-[#F5F7FA] px-3 py-2 text-xs" data-testid={`offer-uploaded-file-${f.id}`}>
                    <FileText size={13} />
                    <span className="max-w-[160px] truncate">{f.original_filename}</span>
                    <button onClick={() => setFiles((arr) => arr.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-red-600 transition-colors duration-200">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button onClick={submit} disabled={busy || uploading} data-testid="submit-offer-button" className="w-full sm:w-auto rounded-none h-12 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
            <Send size={15} /> {busy ? "Gönderiliyor..." : editing ? "Revizyonu Kaydet" : "Teklifi Gönder"}
          </Button>
        </div>
      )}

      <div className="max-w-3xl">
        <MessageThread endpoint={`/supplier/requests/${id}/messages`} myRole="supplier" title="Yöneticiye Sorun" emptyText="Bu taleple ilgili sorularınızı buradan yazabilirsiniz; ekip panelden yanıtlar." placeholder="Sorunuzu veya notunuzu yazın..." />
      </div>
    </div>
  );
}
